import { UserX, Users } from "lucide-react";

import { cn } from "@/lib/utils";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import type { ContractingItem, SearchProfile, TeamMember } from "@/types/domain";

/** `null` = todo el equipo. `"unassigned"` = lo que no lleva nadie. */
export type Segment = string | null | "unassigned";
/** `null` = todas las líneas del usuario. */
export type LineFilter = number | null;
/** El comercial elige entre su cartera y todo lo de sus líneas. */
export type Scope = "mine" | "lines";

export function matchesSegment(item: ContractingItem, segment: Segment) {
  if (segment === null) return true;
  const assigned = item.contractinguser?.assignedUserId ?? null;
  if (segment === "unassigned") return assigned === null;
  return assigned === segment;
}

/** Pastilla de filtro del tablero; también la usa el Mercado de Fianzas para el objeto. */
export function FilterChip({
  active,
  onClick,
  children,
  tone = "default",
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tone?: "default" | "warning" | "dark";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] transition-colors",
        // Inactivo: pastilla blanca de borde tenue. Es la base sobre la que el
        // estado activo se nota sin tener que gritar.
        !active &&
          tone !== "warning" &&
          "border-slate-200 bg-card text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800/60",
        active &&
          tone === "dark" &&
          "border-primary bg-primary font-semibold text-primary-foreground",
        active &&
          tone === "default" &&
          "border-primary/25 bg-primary/10 font-semibold text-primary",
        // "Sin responsable" va punteado y en rojo siempre: es un hueco, no una
        // categoría más. Se marca aunque no esté seleccionada.
        tone === "warning" &&
          "border-dashed border-red-300 text-red-600 dark:border-red-900 dark:text-red-400",
        tone === "warning" &&
          (active
            ? "bg-red-50 font-semibold dark:bg-red-950/30"
            : "bg-card hover:bg-red-50/60 dark:hover:bg-red-950/20"),
      )}
    >
      {children}
    </button>
  );
}

/**
 * El número del chip. Va sólido cuando su chip está activo — es lo que hace que
 * el filtro puesto se lea de un vistazo — y apagado cuando no.
 */
function Count({
  value,
  active,
  tone,
}: {
  value: number;
  active?: boolean;
  tone?: "warning";
}) {
  return (
    <span
      className={cn(
        "rounded-md px-1.5 py-0.5 text-[11px] font-bold tabular-nums",
        tone === "warning"
          ? "text-red-600 dark:text-red-400"
          : active
            ? "bg-primary text-primary-foreground"
            : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
      )}
    >
      {value}
    </span>
  );
}

/** Fila 1: por qué línea de negocio se mira el pipeline. Sin líneas queda solo `children`. */
export function LineRow({
  profiles,
  value,
  onChange,
  leadsTeam,
  hideLines = false,
  children,
}: {
  profiles: SearchProfile[];
  value: LineFilter;
  onChange: (value: LineFilter) => void;
  leadsTeam: boolean;
  hideLines?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className={cn("flex flex-wrap items-center gap-2", hideLines && "hidden")}>
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
          {leadsTeam ? "Línea de negocio" : "Mis líneas"}
        </span>
        <FilterChip active={value === null} tone="dark" onClick={() => onChange(null)}>
          {leadsTeam ? "Todas" : "Todas mis líneas"}
        </FilterChip>
        {profiles.map((profile) => (
          <FilterChip
            key={profile.searchprofile_id}
            tone="dark"
            active={value === profile.searchprofile_id}
            onClick={() => onChange(profile.searchprofile_id)}
          >
            {profile.searchprofile_name}
          </FilterChip>
        ))}
      </div>
      {children}
    </div>
  );
}

/** Fila 2 del que dirige: el reparto por comercial, que además filtra el tablero. */
export function ResponsibleRow({
  items,
  members,
  segment,
  onSegmentChange,
  visibleCount,
  lineCountByUser,
}: {
  items: ContractingItem[];
  members: TeamMember[];
  segment: Segment;
  onSegmentChange: (segment: Segment) => void;
  visibleCount: number;
  /** Sin el mapa no se muestran las líneas de cada uno (Fianzas). */
  lineCountByUser?: Map<string, number>;
}) {
  const countBySegment = new Map<string, number>();
  let unassigned = 0;
  for (const item of items) {
    const assigned = item.contractinguser?.assignedUserId ?? null;
    if (!assigned) unassigned++;
    else countBySegment.set(assigned, (countBySegment.get(assigned) ?? 0) + 1);
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
          Responsables
        </span>

        <FilterChip active={segment === null} onClick={() => onSegmentChange(null)}>
          <Count value={items.length} active={segment === null} />
          <Users className="size-3.5" />
          Todo el equipo
        </FilterChip>

        {members.map((member) => {
          const lines = lineCountByUser?.get(member.userId);
          return (
            <FilterChip
              key={member.userId}
              active={segment === member.userId}
              onClick={() => onSegmentChange(member.userId)}
            >
              <Count
                value={countBySegment.get(member.userId) ?? 0}
                active={segment === member.userId}
              />
              <MemberAvatar
                member={{
                  id: member.userId,
                  name: member.name,
                  image: member.image,
                }}
                className="size-5"
              />
              <span className="font-medium">{member.name}</span>
              {lineCountByUser && (
                <span className="text-[11px] text-slate-400">
                  {lines ?? 0} línea{lines === 1 ? "" : "s"}
                </span>
              )}
            </FilterChip>
          );
        })}

        <FilterChip
          tone="warning"
          active={segment === "unassigned"}
          onClick={() => onSegmentChange("unassigned")}
        >
          <UserX className="size-3.5" />
          Sin responsable
          <Count value={unassigned} tone="warning" />
        </FilterChip>
      </div>

      <span className="text-xs text-slate-400">
        {visibleCount} de {items.length} licitaciones
      </span>
    </div>
  );
}

/** Fila 2 del comercial: su cartera o todo lo de las líneas que trabaja. */
export function ScopeRow({
  scope,
  onScopeChange,
  visibleCount,
}: {
  scope: Scope;
  onScopeChange: (scope: Scope) => void;
  visibleCount: number;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
          Alcance
        </span>
        <FilterChip
          tone="dark"
          active={scope === "mine"}
          onClick={() => onScopeChange("mine")}
        >
          Mis licitaciones
        </FilterChip>
        <FilterChip
          tone="dark"
          active={scope === "lines"}
          onClick={() => onScopeChange("lines")}
        >
          Mis líneas (equipo)
        </FilterChip>
        <span className="text-xs text-slate-400">
          {scope === "mine"
            ? "Ves solo lo que tienes asignado."
            : "Ves todo lo que la empresa trabaja en tus líneas."}
        </span>
      </div>
      <span className="text-xs text-slate-400">
        {visibleCount} licitación{visibleCount === 1 ? "" : "es"} visible
        {visibleCount === 1 ? "" : "s"}
      </span>
    </div>
  );
}
